// https://www.epochconverter.com/ - to convert epoch time to human readable time
import { FC, useLayoutEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "./assets/hooks/hooks";
import { AuthData } from "./redux/auth-action";
import { useNotification } from "./assets/hooks/useNotification";
import { jwtDecode } from "jwt-decode";
import moment from "moment";
import { initializedActions } from "./redux/initial-app-slice";
import { getPermissionsFromToken } from "./assets/helpers/authToken";
import { AccessRule, canAccess } from "./access";

interface ProtectedRouteProps {
    children: React.ReactNode;
    access?: AccessRule;
}

const ProtectedRoute: FC<ProtectedRouteProps> = ({ children, access }) => {
    const navigate = useNavigate();
    const { InformationMessageBox } = useNotification();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const isInitialized = useAppSelector(
        (state) => state.initializeApp.initialized
    );

    let authLocalStorage: AuthData | null = JSON.parse(
        localStorage?.getItem("auth") as string
    );

    if (authLocalStorage?.isAuth) {
        dispatch(
            initializedActions.initializedSuccess({
                userId: authLocalStorage.id,
                token: authLocalStorage.token,
                userName: authLocalStorage.userName,
                role: authLocalStorage.role,
                // Derive from the token (single source of truth) so sessions
                // created before permissions were stored still gate correctly.
                permissions: getPermissionsFromToken(authLocalStorage.token),
                initialized: true,
                theme: "dark",
            })
        );
    }

    useLayoutEffect(() => {
        const token = authLocalStorage?.token;
        if (token) {
            const { exp } = jwtDecode<{ exp: number }>(token);
            const currentTime = moment().unix();

            if (currentTime > exp) {
                console.log("Token has expired!");
                localStorage.removeItem("auth");

                InformationMessageBox({
                    textContent: {
                        title: "Token has expired!",
                        text: "Please re-authorize",
                    },
                }).then(() => {
                    navigate("/auth");
                });
            }
        }
    }, [
        authLocalStorage,
        isInitialized,
        navigate,
        InformationMessageBox,
        location,
    ]);

    if (!authLocalStorage?.isAuth) {
        return <Navigate to="/auth" />;
    }

    if (
        !canAccess(
            {
                role: authLocalStorage.role,
                permissions: getPermissionsFromToken(authLocalStorage.token),
            },
            access
        )
    ) {
        return <Navigate to="/" replace />;
    }

    return <>{children}</>;
};

export default ProtectedRoute;
